import React, { createContext, useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import api from './services/api';
import { createPartnerLoginBody } from './lib/turnstileState';
import { forgetPendingPhone, pendingPhoneFor, rememberPendingPhone } from './lib/phoneVerificationPending';
import { nurDatum } from './lib/zeit';
import Login from './pages/Login';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Drivers from './pages/Drivers';
import DriverProfile from './pages/DriverProfile';
import Vehicles from './pages/Vehicles';
import Documents from './pages/Documents';
import Performance from './pages/Performance';
import Reports from './pages/Reports';
import Earnings from './pages/Earnings';
import LiveMap from './pages/LiveMap';
import Promotions from './pages/Promotions';
import Settings from './pages/Settings';
import Balance from './pages/Balance';
import Invoices from './pages/Invoices';
import Inbox from './pages/Inbox';
import DriverStats from './pages/DriverStats';
import DriverPerformance from './pages/DriverPerformance';
import Reservations from './pages/Reservations';
import OrderLog from './pages/OrderLog';
import Dispatch from './pages/Dispatch';
import LegalPage from './pages/Legal';
import PartnerLegalUpdate from './pages/PartnerLegalUpdate';
import CommandPalette from './components/CommandPalette';
import { PARTNER_NAV_GROUPS, PARTNER_NAV_LINKS, filterPartnerNavigation, getPartnerNavigationContext } from './navigation';
import {
  readSeenReservationKeys,
  reservationAttentionKeys,
  reservationSeenStorageKey,
  unseenReservationCount,
  writeSeenReservationKeys,
} from './lib/reservationAttention';
import { AlertTriangle, BarChart2, ChevronDown, Clock, LogOut, Menu, MessageCircle, Search, Settings as SettingsIcon, ShieldCheck, X, XCircle } from 'lucide-react';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const ReservationAttentionContext = createContext({ unseenReservations: 0 });
export const useReservationAttention = () => useContext(ReservationAttentionContext);

export function validPartnerOnboardingState(value) {
  return value && value.role === 'partner' && typeof value.canOperate === 'boolean' ? value : null;
}

// Portalweite Naht: Sie lebt oberhalb der einzelnen Route/Layout-Instanzen und verliert
// ihren Stand deshalb nicht bei jedem Seitenwechsel. Der Serverleser ist bereits exakt
// auf das eingeloggte Partnerprofil gescopt; der lokale Gesehen-Stand zusätzlich auf
// partner.id. Ein fehlgeschlagener Poll löscht niemals eine zuvor sichtbare Zahl.
export function ReservationAttentionProvider({ partnerId, children, pollMs = 6000 }) {
  const location = useLocation();
  const viewingReservations = location.pathname === '/reservations';
  const storageKey = useMemo(() => reservationSeenStorageKey(partnerId), [partnerId]);
  const seenRef = useRef(new Set());
  const currentKeysRef = useRef([]);
  const viewingRef = useRef(viewingReservations);
  const requestSequenceRef = useRef(0);
  const hydratedStorageKeyRef = useRef(null);
  const [unseenReservations, setUnseenReservations] = useState(0);

  const markSeen = useCallback((keys) => {
    (Array.isArray(keys) ? keys : []).forEach((key) => seenRef.current.add(key));
    if (typeof window !== 'undefined') {
      writeSeenReservationKeys(window.localStorage, storageKey, seenRef.current);
    }
    setUnseenReservations(0);
  }, [storageKey]);

  useEffect(() => {
    viewingRef.current = viewingReservations;
    // Beim Direktaufruf von /reservations läuft dieser Effekt vor der Hydrierung.
    // Erst ein für genau dieses Partnerkonto geladener Speicherstand darf geschrieben
    // werden; sonst würde ein leerer Initial-Set die bisherige Historie zerstören.
    if (viewingReservations && hydratedStorageKeyRef.current === storageKey) {
      markSeen(currentKeysRef.current);
    }
  }, [markSeen, storageKey, viewingReservations]);

  useEffect(() => {
    let active = true;
    seenRef.current = typeof window === 'undefined'
      ? new Set()
      : readSeenReservationKeys(window.localStorage, storageKey);
    hydratedStorageKeyRef.current = storageKey;
    currentKeysRef.current = [];
    setUnseenReservations(0);

    const refresh = async () => {
      const requestSequence = ++requestSequenceRef.current;
      try {
        const response = await api.get('/partners/reservations');
        if (!active || requestSequence !== requestSequenceRef.current) return;
        const keys = reservationAttentionKeys(response.data);
        currentKeysRef.current = keys;
        if (viewingRef.current) markSeen(keys);
        else setUnseenReservations(unseenReservationCount(keys, seenRef.current));
      } catch {
        // Stale-but-honest: ein Netzfehler darf eine bereits gemeldete Zahl nicht nullen.
      }
    };

    refresh();
    const timer = window.setInterval(refresh, pollMs);
    return () => {
      active = false;
      window.clearInterval(timer);
      if (hydratedStorageKeyRef.current === storageKey) hydratedStorageKeyRef.current = null;
    };
  }, [markSeen, pollMs, storageKey]);

  const value = useMemo(() => ({ unseenReservations }), [unseenReservations]);
  return <ReservationAttentionContext.Provider value={value}>{children}</ReservationAttentionContext.Provider>;
}

// Handy-Erkennung: Portal kompakter darstellen, wenn vom Smartphone-Browser geöffnet.
function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const f = () => setM(window.innerWidth < 640);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);
  return m;
}

function AvatarMenu() {
  const { user, partner, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const initial = (partner?.company_name || user?.name || 'U').charAt(0).toUpperCase();
  return (
    <div ref={ref} className="vg-avatar-menu">
      <button className="vg-avatar-trigger" onClick={() => setOpen(o => !o)} aria-label="Kontomenü öffnen" aria-expanded={open}>
        <div className="vg-avatar">{initial}</div>
        <span className="vg-avatar-copy">
          <strong>{partner?.company_name || user?.name}</strong>
          <small>Partnerkonto</small>
        </span>
        <ChevronDown size={16} color="#4b5563" />
      </button>
      {open && (
        <div className="vg-avatar-popover">
          <div className="vg-avatar-popover-head">
            <strong>{partner?.company_name || user?.name}</strong>
            <span>{user?.email}</span>
          </div>
          {[
            { icon: BarChart2, label: 'Fahrer-Umsatz', to: '/driver-stats' },
            { icon: SettingsIcon, label: 'Einstellungen', to: '/settings' },
          ].map(({ icon: Icon, label, to }) => (
            <button className="vg-avatar-action" key={to} onClick={() => { setOpen(false); navigate(to); }}>
              <Icon size={16} /> {label}
            </button>
          ))}
          <button className="vg-avatar-action is-danger" onClick={() => { logout(); navigate('/login'); }}>
            <LogOut size={16} /> Abmelden
          </button>
        </div>
      )}
    </div>
  );
}

export function Sidebar({ open, setOpen, mobile }) {
  const { unseenReservations } = useReservationAttention();
  const [grp, setGrp] = useState(() => { try { return JSON.parse(localStorage.getItem('pp_nav_groups') || '{}'); } catch { return {}; } });
  const [navQuery, setNavQuery] = useState('');
  const toggleGrp = (id) => setGrp(c => { const next = { ...c, [id]: !c[id] }; try { localStorage.setItem('pp_nav_groups', JSON.stringify(next)); } catch {} return next; });
  const expanded = mobile ? true : open;                 // auf dem Handy immer mit Beschriftung (Off-Canvas-Drawer)
  const visibleLinks = filterPartnerNavigation(PARTNER_NAV_LINKS, navQuery);
  const closeOnNav = () => { setNavQuery(''); if (mobile) setOpen(false); };

  // ⌘K gehört jetzt der globalen Command-Palette (im Layout) — die Sidebar-Suche bleibt für die Maus.
  return (
    <aside className={`vg-sidebar ${expanded ? 'is-expanded' : 'is-collapsed'} ${mobile ? 'is-mobile' : ''} ${open ? 'is-open' : ''}`}>
      <div className="vg-sidebar-brand">
        {expanded && (
          <div className="vg-wordmark-wrap">
            <span className="vg-wordmark">V<span>OO</span>GO</span>
            <span className="vg-product-label">Partner Workspace</span>
          </div>
        )}
        <button className="vg-icon-button" onClick={() => mobile ? setOpen(false) : setOpen(o => !o)} title={mobile ? 'Schließen' : (open ? 'Einklappen' : 'Ausklappen')} aria-label={mobile ? 'Navigation schließen' : (open ? 'Navigation einklappen' : 'Navigation ausklappen')}>
          {(mobile || open) ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>
      {expanded && (
        <label className="vg-nav-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={navQuery}
            onChange={(event) => setNavQuery(event.target.value)}
            placeholder="Bereiche durchsuchen"
            aria-label="Partner-Bereiche durchsuchen"
          />
        </label>
      )}
      <nav className="vg-sidebar-nav" aria-label="Partner-Navigation">
        {PARTNER_NAV_GROUPS.map((g) => {
          const items = visibleLinks.filter((n) => n.group === g.id);
          if (!items.length) return null;
          const gopen = navQuery ? true : !grp[g.id];
          return (
            <section className="vg-nav-section" key={g.id}>
              {expanded ? (
                <button className="pp-grp" onClick={() => toggleGrp(g.id)} title={gopen ? 'Gruppe einklappen' : 'Gruppe ausklappen'} aria-expanded={gopen}>
                  <span className="pp-grp-label">{g.label}</span>
                  <ChevronDown className="pp-chev" size={14} style={{ transform: gopen ? 'none' : 'rotate(-90deg)' }} />
                </button>
              ) : (
                <div className="vg-nav-divider" />
              )}
              {(gopen || !expanded) && items.map(({ to, label, end, badge, attention, icon: Icon }) => {
                const runtimeBadge = attention === 'reservations' ? unseenReservations : 0;
                const hasRuntimeBadge = Number.isFinite(runtimeBadge) && runtimeBadge > 0;
                const runtimeLabel = `${runtimeBadge} neue Reservierungen`;
                return (
                  <NavLink key={to} to={to} end={end}
                    title={hasRuntimeBadge ? `${label} – ${runtimeLabel}` : label}
                    onClick={closeOnNav}
                    className={({ isActive }) => 'pp-item' + (isActive ? ' active' : '')}
                    style={!expanded ? { justifyContent: 'center', position: 'relative' } : { position: 'relative' }}>
                    {Icon && <Icon size={19} strokeWidth={1.9} />}
                    {expanded && <span className="vg-nav-label">{label}</span>}
                    {hasRuntimeBadge && (
                      <span className={`vg-nav-badge${expanded ? '' : ' is-compact'}`} aria-label={runtimeLabel}>
                        {runtimeBadge}
                      </span>
                    )}
                    {!hasRuntimeBadge && expanded && badge && <span className="vg-nav-badge">2</span>}
                    {!hasRuntimeBadge && !expanded && badge && <span className="vg-nav-badge-dot" />}
                  </NavLink>
                );
              })}
            </section>
          );
        })}
        {expanded && visibleLinks.length === 0 && (
          <div className="vg-nav-empty">Kein Bereich gefunden.</div>
        )}
      </nav>
      {expanded && (
        <div className="pp-sidebar-trust">
          <span className="pp-sidebar-trust-icon"><ShieldCheck size={16} /></span>
          <span>
            <strong>Eigener Flottenbereich</strong>
            <small>Strikt vom VOOGO Admin getrennt</small>
          </span>
        </div>
      )}
    </aside>
  );
}

export function SupportTopbarLink() {
  return (
    <NavLink to="/inbox" className="pp-support-link" aria-label="Support und Postfach öffnen">
      <MessageCircle size={16} />
      <span>Support</span>
    </NavLink>
  );
}

// Ablauf-Wächter (⑰): Banner — ROT bei automatischer Sperre (Konzession abgelaufen / Fahrer
// gesperrt), GELB bei anstehenden Dokument-Abläufen (Warn-Staffel). Daten: GET /partners/expiry.
function ExpiryBanner() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get('/partners/expiry').then(r => setSt(r.data)).catch(() => {});
  }, []);
  if (!st) return null;
  const bar = (tone, content) => (
    <div className={`pp-notice-bar is-${tone}`}>{content}</div>
  );
  // ADMIN-FAHRZEUG-PARTNER-SPERRE-001: Admin-Sperre zeigt den Grund des Admins und führt zu Dokumenten/Einstellungen.
  if (st.suspended_reason && st.suspension?.kind === 'admin') {
    return bar('danger', <>
      <XCircle size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
      {st.suspension_message || 'Dein Betrieb wurde von VOOGO gesperrt'} — keine Vermittlung, Fahrer können nicht online gehen.
      {' '}Bitte reiche die angeforderten <NavLink to="/documents">Unterlagen</NavLink> ein oder korrigiere deine <NavLink to="/settings">Firmenangaben</NavLink>; wir heben die Sperre nach der Prüfung auf.
    </>);
  }
  if (st.suspended_reason) {
    return bar('danger', <>
      <XCircle size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
      Konto gesperrt: Die Konzession/Genehmigungsurkunde (§ 49) ist abgelaufen — keine Vermittlung, Fahrer können nicht online gehen.
      {' '}<NavLink to="/documents">Jetzt gültige Urkunde hochladen</NavLink> — nach der Freigabe wird das Konto automatisch entsperrt.
    </>);
  }
  if (st.suspended_drivers?.length) {
    return bar('danger', <>
      <XCircle size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
      {st.suspended_drivers.length === 1
        ? `Fahrer ${st.suspended_drivers[0].name} ist gesperrt (${st.suspended_drivers[0].suspension?.short || 'Dokument abgelaufen'}).`
        : `${st.suspended_drivers.length} Fahrer sind gesperrt (${[...new Set(st.suspended_drivers.map(d => d.suspension?.short || 'Dokument abgelaufen'))].join(', ')}).`}
      {' '}{st.suspended_drivers.every(d => !d.suspension || d.suspension.kind === 'dokument')
        ? 'Neue Dokumente hochladen — Entsperrung erfolgt automatisch nach der Freigabe.'
        : 'Bei einer Sperre durch VOOGO entscheidet der Support — bitte wende dich an support@voogo.de.'}
    </>);
  }
  if (st.warnings?.length) {
    const w = st.warnings[0];   // dringendste zuerst (Server sortiert)
    const wer = w.scope === 'driver' ? `${w.driver_name}: ` : '';
    const wann = w.days_left <= 0 ? 'HEUTE' : `in ${w.days_left} Tag${w.days_left === 1 ? '' : 'en'}`;
    return bar('warning', <>
      <Clock size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
      {wer}{w.label} läuft {wann} ab ({nurDatum(w.expires_at)}).
      {st.warnings.length > 1 && ` +${st.warnings.length - 1} weitere Dokumente laufen bald ab.`}
      {' '}Bitte rechtzeitig erneuern — nach Ablauf sperrt das System automatisch.
    </>);
  }
  return null;
}

function Layout({ children, fullBleed }) {
  const { partner, onboarding } = useAuth();
  const mobile = useIsMobile();
  const location = useLocation();
  const [open, setOpen] = useState(!mobile);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // ⌘K/Ctrl+K öffnet die globale Command-Palette — von JEDEM Screen, unabhängig von der Sidebar.
  useEffect(() => {
    const onShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase('de') === 'k') {
        event.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onShortcut);
    return () => window.removeEventListener('keydown', onShortcut);
  }, []);

  const name = partner?.company_name || 'VOOGO Partner';
  const { item: cur, group } = getPartnerNavigationContext(location.pathname);
  const pageTitle = location.pathname === '/' ? 'Übersicht' : (cur ? cur.label : 'Partner-Portal');
  const pageDescription = cur?.description || 'Sicherer Arbeitsbereich für deine eigene Flotte.';
  const routeKey = location.pathname === '/' ? 'home' : location.pathname.replace(/^\/+|\/+$/g, '').replace(/[^a-z0-9-]+/gi, '-') || 'home';
  return (
    <div className="vg-portal-shell">
      <Sidebar open={open} setOpen={setOpen} mobile={mobile} />
      {mobile && open && <button className="vg-mobile-scrim" onClick={() => setOpen(false)} aria-label="Navigation schließen" />}
      <div className="vg-shell-content">
      <header className="vg-topbar">
        <div className="vg-topbar-title-wrap">
          {mobile && <button onClick={() => setOpen(true)} aria-label="Menü öffnen" className="vg-icon-button"><Menu size={22} /></button>}
          <div className="vg-page-heading">
            <span className="vg-page-context">Partnerportal <b>/</b> {group?.label || 'Workspace'}</span>
            <strong>{pageTitle}</strong>
            <span className="vg-page-description">{pageDescription}</span>
          </div>
        </div>
        <div className="pp-topbar-actions">
          <button type="button" className="vg-cmdk-trigger" onClick={() => setPaletteOpen(true)} title="Suchen (⌘K)" aria-label="Suchen (⌘K)">
            <Search size={15} aria-hidden="true" />
            <span className="vg-cmdk-trigger-label">Suchen</span>
            <kbd>⌘K</kbd>
          </button>
          <SupportTopbarLink />
          <div className="pp-workspace-state" aria-label="Partnerkonto freigegeben">
            <span className="pp-live-dot" />
            <span><strong>Flotte aktiv</strong><small>{name}</small></span>
          </div>
          <AvatarMenu />
        </div>
      </header>
      {partner?.approval_status === 'pending' && (
        <div className="pp-notice-bar is-neutral">
          <Clock size={15} />
          <span>
            Dein Konto wird geprüft. Nach Freigabe durch den Admin erhältst du Vollzugriff.
          </span>
        </div>
      )}
      {partner?.approval_status === 'rejected' && (
        <div className="pp-notice-bar is-danger">
          <span>
            <XCircle size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> Dein Antrag wurde abgelehnt: {partner.rejection_reason}
          </span>
        </div>
      )}
      {partner?.approval_status === 'approved' && <ExpiryBanner />}
      {/* AGB-122-STORNO-LIVE: neue Fassung ohne Portalsperre – Hinweis, Annahme nachholbar. */}
      {onboarding?.legalAcceptancePending === true && location.pathname !== '/vertragsunterlagen' && (
        <div className="pp-notice-bar is-warning" data-testid="legal-update-banner">
          <span>
            <ShieldCheck size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> Neue Fassung der Partner-Vertragsunterlagen
            {onboarding.agbVersion ? ` (Version ${onboarding.agbVersion})` : ''}. Dein Portal bleibt nutzbar. Bitte prüfe und bestätige die neue Fassung.
            {' '}<NavLink to="/vertragsunterlagen">Jetzt prüfen</NavLink>
          </span>
        </div>
      )}
      {/* ADMIN-FAHRZEUG-PARTNER-SPERRE-001: offene Stammdaten-Nachforderung des Admins (Steuernummer/Adresse …). */}
      {partner?.data_correction_request && (
        <div className="pp-notice-bar is-warning" data-testid="data-correction-banner">
          <span>
            <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> Bitte Firmenangaben korrigieren: {partner.data_correction_request}
            {' '}<NavLink to="/settings">Zu den Einstellungen</NavLink>
          </span>
        </div>
      )}
      {/* key=Pfad → jede Seite blendet sanft ein (.vg-page, reine CSS-Animation) */}
      <main className={fullBleed ? 'vg-shell-main is-full-bleed' : 'vg-shell-main'}>
        <div key={location.pathname} className="vg-page pp-page" data-page={routeKey} data-group={group?.id || 'workspace'}>{children}</div>
      </main>
      </div>
      {/* Vor der Freigabe ist das Portal ohnehin gesperrt — Palette dann nur Bereiche, keine Datensuche. */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} dataEnabled={partner?.approval_status === 'approved'} />
    </div>
  );
}

function AppRoutes() {
  const { user, partner, onboarding, partnerProfileMissing, pendingPhoneVerification, loading, sessionError, restoreSession, logout, refreshPartner } = useAuth();
  if (loading) return (
    <div className="pp-loading-screen">
      <div className="pp-loading-card">
        <div className="pp-loading-mark">V<span>OO</span>GO</div>
        <div className="pp-loading-line"><span /> Partner Workspace wird geladen</div>
      </div>
    </div>
  );
  if (sessionError) return (
    <div className="pp-loading-screen" role="alert">
      <div className="pp-loading-card">
        <div className="pp-loading-mark">V<span>OO</span>GO</div>
        <div className="pp-loading-line">Deine Sitzung konnte gerade nicht geprüft werden.</div>
        <button type="button" onClick={restoreSession}>Erneut prüfen</button>
        <button type="button" onClick={logout}>Abmelden</button>
      </div>
    </div>
  );
  if (!user) return <Routes><Route path="/login" element={<Login />} /><Route path="/register" element={<Register />} /><Route path="*" element={<Navigate to="/login" />} /></Routes>;
  // Ein Partnerprofil allein macht aus einem bestehenden Kunden-JWT keinen Partner-JWT.
  // Der rollenverzweigte Support-Chat darf deshalb erst hinter einem nachweislich als
  // Partner ausgestellten Token erreichbar sein. Die Rolle wird nie im Request behauptet.
  if (user.role !== 'partner') return <PartnerRoleGate />;
  if (pendingPhoneVerification) return <Routes><Route path="/setup" element={<Register key={user.id} setup />} /><Route path="*" element={<Navigate to="/setup" replace />} /></Routes>;
  if (partnerProfileMissing) {
    return <Routes><Route path="/setup" element={<Register key={user.id} setup />} /><Route path="*" element={<Navigate to="/setup" />} /></Routes>;
  }
  if (!partner || !onboarding) {
    return (
      <div className="pp-loading-screen" role="alert">
        <div className="pp-loading-card">
          <div className="pp-loading-mark">V<span>OO</span>GO</div>
          <div className="pp-loading-line">Partnerstatus ist derzeit nicht verfügbar.</div>
          <button type="button" onClick={refreshPartner}>Erneut prüfen</button>
          <button type="button" onClick={logout}>Abmelden</button>
        </div>
      </div>
    );
  }
  // Einziger Workspace-Schalter ist der serverseitig berechnete Betriebsstatus. Dadurch
  // sperrt eine neue Vertragsgeneration ein bereits freigegebenes Konto wieder ins Onboarding.
  if (onboarding.canOperate !== true) return <Routes><Route path="/onboarding" element={<Onboarding />} /><Route path="*" element={<Navigate to="/onboarding" />} /></Routes>;
  return (
    <ReservationAttentionProvider partnerId={partner.id || user.id}>
      <Routes>
        <Route path="/"            element={<Layout><Dashboard /></Layout>} />
        <Route path="/drivers"     element={<Layout><Drivers /></Layout>} />
        <Route path="/dispatch"    element={<Layout><Dispatch /></Layout>} />
        <Route path="/reservations" element={<Layout><Reservations /></Layout>} />
        <Route path="/drivers/:id" element={<Layout><DriverProfile /></Layout>} />
        <Route path="/vehicles"    element={<Layout><Vehicles /></Layout>} />
        <Route path="/order-log"   element={<Layout><OrderLog /></Layout>} />
        <Route path="/documents"   element={<Layout><Documents /></Layout>} />
        <Route path="/performance" element={<Layout><Performance /></Layout>} />
        <Route path="/driver-performance" element={<Layout><DriverPerformance /></Layout>} />
        <Route path="/reports"     element={<Layout><Reports /></Layout>} />
        <Route path="/earnings"    element={<Layout><Earnings /></Layout>} />
        <Route path="/live-map"    element={<Layout fullBleed><LiveMap /></Layout>} />
        <Route path="/promotions"  element={<Layout><Promotions /></Layout>} />
        <Route path="/balance"     element={<Layout><Balance /></Layout>} />
        <Route path="/settlements" element={<Layout><Invoices /></Layout>} />
        <Route path="/invoices"    element={<Navigate to="/settlements" replace />} />
        <Route path="/inbox"       element={<Layout><Inbox /></Layout>} />
        <Route path="/legal"       element={<Layout><LegalPage /></Layout>} />
        <Route path="/vertragsunterlagen" element={<Layout><PartnerLegalUpdate /></Layout>} />
        <Route path="/driver-stats" element={<Layout><DriverStats /></Layout>} />
        <Route path="/settings"    element={<Layout><Settings /></Layout>} />
        <Route path="*"            element={<Navigate to="/" />} />
      </Routes>
    </ReservationAttentionProvider>
  );
}

export function PartnerRoleGate() {
  const { logout } = useAuth();
  useEffect(() => { logout(); }, [logout]);
  return (
    <div className="pp-loading-screen" role="status">
      <div className="pp-loading-card">
        <div className="pp-loading-mark">V<span>OO</span>GO</div>
        <div className="pp-loading-line"><span /> Partner-Zugang wird neu angemeldet</div>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser]       = useState(null);
  const [partner, setPartner] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(false);
  const sessionRequest = useRef(0);
  const [partnerProfileMissing, setPartnerProfileMissing] = useState(false);
  const [pendingPhoneVerification, setPendingPhoneVerification] = useState(null);   // WHATSAPP-OTP-STANDARD-001
  const partnerRefreshSequenceRef = useRef(0);

  const refreshPartner = useCallback(async () => {
    const requestSequence = ++partnerRefreshSequenceRef.current;
    setPartnerProfileMissing(false);
    try {
      const partnerResponse = await api.get('/partners/me', { timeout: 10000 });
      if (requestSequence !== partnerRefreshSequenceRef.current) return null;
      setPartner(partnerResponse.data.partner);
      try {
        const onboardingResponse = await api.get('/partners/onboarding', { timeout: 10000 });
        if (requestSequence !== partnerRefreshSequenceRef.current) return null;
        const nextOnboarding = validPartnerOnboardingState(onboardingResponse.data);
        setOnboarding(nextOnboarding);
        return nextOnboarding ? { partner: partnerResponse.data.partner, onboarding: nextOnboarding } : null;
      } catch {
        if (requestSequence !== partnerRefreshSequenceRef.current) return null;
        setOnboarding(null);
        return null;
      }
    } catch (error) {
      if (requestSequence !== partnerRefreshSequenceRef.current) return null;
      setPartner(null);
      setOnboarding(null);
      setPartnerProfileMissing(error.response?.status === 404
        && error.response?.data?.error === 'Kein Partnerprofil');
      return null;
    }
  }, []);

  const restoreSession = useCallback(async () => {
    const request = ++sessionRequest.current;
    partnerRefreshSequenceRef.current += 1;
    const token = localStorage.getItem('partner_token');
    setUser(null); setPartner(null); setOnboarding(null); setPartnerProfileMissing(false);
    setPendingPhoneVerification(null);
    setSessionError(false); setLoading(true);
    if (!token) { setLoading(false); return; }
    const current = () => request === sessionRequest.current && localStorage.getItem('partner_token') === token;
    try {
      const response = await api.get('/auth/me', { timeout: 10000 });
      if (!current()) return;
      const confirmed = response.data?.user;
      if (!confirmed?.id || !confirmed?.role) throw new Error('session_response_invalid');
      setUser({ ...confirmed, roles: response.data.roles || confirmed.roles || [] });
      setPendingPhoneVerification(pendingPhoneFor(confirmed));   // Pflicht-Telefoncode auch nach Neuladen
      if (confirmed.role === 'partner') await refreshPartner();
    } catch (error) {
      if (request !== sessionRequest.current) return;
      const stored = localStorage.getItem('partner_token');
      if (stored !== token && stored !== null) return;
      if (error.response?.status === 401) localStorage.removeItem('partner_token');
      else if (stored === token) setSessionError(true);
    } finally {
      if (request === sessionRequest.current) {
        if (localStorage.getItem('partner_token') && !current()) setSessionError(true);
        setLoading(false);
      }
    }
  }, [refreshPartner]);

  useEffect(() => {
    restoreSession();
    return () => { sessionRequest.current += 1; partnerRefreshSequenceRef.current += 1; };
  }, [restoreSession]);

  const sessionVersion = sessionRequest.current;
  const sessionToken = localStorage.getItem('partner_token');
  const isCurrentSession = () => sessionVersion === sessionRequest.current
    && sessionToken === localStorage.getItem('partner_token');

  const applySession = async (data) => {
    const request = ++sessionRequest.current;
    setSessionError(false); setLoading(false);
    partnerRefreshSequenceRef.current += 1;
    setPartner(null);
    setOnboarding(null);
    setPartnerProfileMissing(false);
    localStorage.setItem('partner_token', data.token);
    setUser({ ...data.user, roles: data.roles || data.user?.roles || [] });
    if (data.user?.role === 'partner') await refreshPartner();
    return request === sessionRequest.current && localStorage.getItem('partner_token') === data.token ? data : false;
  };
  // `extra`: Code-Schritt der SMS-Zwei-Faktor-Anmeldung (siehe createPartnerLoginBody).
  const partnerLogin = async (email, password, turnstile_token, extra = null, stillActive = () => true) => {
    const request = ++sessionRequest.current;
    partnerRefreshSequenceRef.current += 1;
    const token = localStorage.getItem('partner_token');
    const current = () => request === sessionRequest.current
      && localStorage.getItem('partner_token') === token && stillActive();
    try {
      const res = await api.post('/auth/login', createPartnerLoginBody(email, password, turnstile_token, extra), { timeout: 15000 });
      if (!current()) return false;
      setPendingPhoneVerification(pendingPhoneFor(res.data?.user));   // offene Pflicht aus der Registrierung
      const accepted = await applySession(res.data);
      return stillActive() ? accepted : false;
    } catch (error) {
      if (!current()) return false;
      throw error;
    }
  };
  // /auth/register already authenticates the newly created, separate partner account.
  // Its session must not be replaced by a second login with a missing/consumed captcha.
  // WHATSAPP-OTP-STANDARD-001: Hat die Registrierung einen Telefoncode verschickt, merkt sich die App
  // den Code-Schritt hier — nach applySession wechselt das Routing auf /setup (neue Register-Instanz).
  const acceptPartnerRegistration = (data, { phoneVerification = null } = {}) => {
    if (!isCurrentSession()) return false;
    if (data?.user?.role !== 'partner' || typeof data?.token !== 'string' || !data.token) {
      throw new Error('Die Registrierung ist erfolgt. Bitte melde dich über „Anmelden“ an.');
    }
    if (phoneVerification) rememberPendingPhone(data.user.id, phoneVerification.target);
    setPendingPhoneVerification(phoneVerification || pendingPhoneFor(data.user));
    return applySession(data);
  };
  // Nur nach bestätigter Nummer (Owner 05.10.: kein „Später").
  const finishPhoneVerification = () => {
    if (!isCurrentSession()) return false;
    forgetPendingPhone(); setPendingPhoneVerification(null); return true;
  };

  const logout = useCallback(() => {
    sessionRequest.current += 1;
    setSessionError(false); setLoading(false);
    partnerRefreshSequenceRef.current += 1;
    api.post('/auth/logout').catch(() => {}); // Datei-Zugriffs-Cookie serverseitig löschen
    localStorage.removeItem('partner_token'); setUser(null); setPartner(null); setOnboarding(null); setPartnerProfileMissing(false);
    setPendingPhoneVerification(null);
  }, []);

  // Eine neue aktive Rechtsgeneration muss auch ohne Seitenwechsel zeitnah sperren. Fehler
  // setzen den Betriebsnachweis bewusst auf unbekannt (fail-closed), statt alte Freigabe zu behalten.
  useEffect(() => {
    if (user?.role !== 'partner' || !partner?.id) return undefined;
    const refreshWhenVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') refreshPartner();
    };
    const timer = window.setInterval(refreshPartner, 15000);
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [partner?.id, refreshPartner, user?.role]);

  return (
    <AuthContext.Provider value={{ user, partner, onboarding, partnerProfileMissing, loading, sessionError, restoreSession, acceptPartnerRegistration, partnerLogin, logout, refreshPartner, setPartner, pendingPhoneVerification, finishPhoneVerification, isCurrentSession }}>
      <BrowserRouter basename="/">
        <Toaster position="top-right" toastOptions={{ style: { borderRadius: 10, fontFamily: 'Inter, sans-serif' } }} />
        <AppRoutes />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}
